/** Lo que guarda cada tabla en localStorage (`ndw-tables-<modelo>`): todas las columnas del
 * selector, en el orden elegido y con si se ven o no */
export interface ColumnaGuardada {
    key: string;
    shown: boolean;
}

/**
 * Combina las columnas que la tabla puede mostrar con la configuración que guardó el usuario.
 *
 * Como lo guardado lleva todas las columnas del selector, una columna que no aparece ahí es que no
 * existía (o no se podía mostrar) cuando se guardó: sale según su valor por defecto, y en el orden
 * detrás de la que la precede en la definición de la tabla. Sin esto, quien ya hubiera tocado el
 * selector no vería nunca una columna nueva. Lo guardado de columnas que ya no se pueden mostrar
 * se ignora.
 */
export function combinarConColumnasGuardadas<T extends { key: string, visiblePorDefecto: boolean }>(
    columnas: T[],
    guardadas: ColumnaGuardada[]
): { orden: T[], mostradas: T[] } {
    const orden: T[] = [];
    for (const guardada of guardadas) {
        const columna = columnas.find(c => c.key === guardada?.key);
        if (columna && !orden.includes(columna)) {
            orden.push(columna);
        }
    }
    columnas.forEach((columna, i) => {
        if (orden.includes(columna)) {
            return;
        }
        const anterior = columnas.slice(0, i).reverse().find(c => orden.includes(c));
        orden.splice(anterior ? orden.indexOf(anterior) + 1 : 0, 0, columna);
    });
    const mostradas = orden.filter(columna => {
        const guardada = guardadas.find(g => g?.key === columna.key);
        return guardada ? guardada.shown : columna.visiblePorDefecto;
    });
    return {orden, mostradas};
}

/**
 * Mueve `columna` justo detrás de `detrasDe` en una configuración guardada, conservando si se ve y el
 * orden del resto. Devuelve la configuración nueva, o null si no hay nada que cambiar (falta alguna de
 * las dos columnas o ya estaba en su sitio).
 */
export function reubicarColumnaGuardada(guardadas: ColumnaGuardada[], columna: string, detrasDe: string): ColumnaGuardada[] | null {
    const indice = guardadas.findIndex(g => g?.key === columna);
    const indiceAnterior = guardadas.findIndex(g => g?.key === detrasDe);
    if (indice < 0 || indiceAnterior < 0 || indice === indiceAnterior + 1) {
        return null;
    }
    const sinColumna = guardadas.filter((_, i) => i !== indice);
    const destino = sinColumna.findIndex(g => g?.key === detrasDe) + 1;
    return [...sinColumna.slice(0, destino), guardadas[indice], ...sinColumna.slice(destino)];
}

/**
 * Enciende `columna` en una configuración guardada, conservando el orden y el resto. Devuelve la
 * configuración nueva, o null si no hay nada que cambiar (no está guardada o ya se veía).
 */
export function mostrarColumnaGuardada(guardadas: ColumnaGuardada[], columna: string): ColumnaGuardada[] | null {
    const guardada = guardadas.find(g => g?.key === columna);
    if (!guardada || guardada.shown) {
        return null;
    }
    return guardadas.map(g => g === guardada ? {...g, shown: true} : g);
}
